'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X, LogOut, User } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { getSession, clearSession, UserRole } from '@/lib/session';
import { createClient } from '@/lib/supabase/client';

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [userName, setUserName] = useState<string>('');
  const pathname = usePathname();
  const isLanding = pathname === '/';

  useEffect(() => {
    const session = getSession();
    if (session?.role) {
      setUserRole(session?.role);
      setUserName(session?.name || '');
    }
  }, []);

  const handleLogout = async () => {
    const supabase = createClient();
    await supabase?.auth?.signOut();
    clearSession();
    sessionStorage.removeItem('ofy_role_chosen');
    window.location.href = '/';
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-border">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none">
              <span className="font-extrabold text-xl tracking-tight gradient-brand-text">OFY</span>
              {isLanding && (
                <span className="text-[10px] text-muted-foreground font-medium tracking-wide">Offers For You</span>
              )}
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
          </nav>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-3">
            {userRole ? (
              <div className="flex items-center gap-3">
                {userRole === 'customer' ? (
                  <Link
                    href="/customer-settings"
                    className="flex items-center gap-2 text-sm text-foreground hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center">
                      <User size={14} className="text-white" />
                    </div>
                    {userName && <span className="font-medium">{userName}</span>}
                  </Link>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <div className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center">
                      <User size={14} className="text-white" />
                    </div>
                    {userName && <span className="font-medium">{userName}</span>}
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            ) : null}
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} className="text-foreground" /> : <Menu size={22} className="text-foreground" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-border bg-white px-4 py-4 flex flex-col gap-2">
          {userRole ? (
            <>
              {userRole === 'customer' && (
                <Link
                  href="/customer-settings"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <div className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center shrink-0">
                    <User size={14} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{userName || 'My Profile'}</p>
                    <p className="text-xs text-muted-foreground">View & edit profile</p>
                  </div>
                </Link>
              )}
              {userRole !== 'customer' && userName && (
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center shrink-0">
                    <User size={14} className="text-white" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">{userName}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="btn-outline text-sm w-full flex items-center justify-center gap-2 mt-1"
              >
                <LogOut size={15} />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link href="/customer-sign-up-login" className="btn-outline text-sm w-full text-center py-2.5" onClick={() => setOpen(false)}>Customer Sign In</Link>
              <Link href="/business-sign-up-login" className="btn-primary text-sm w-full text-center py-2.5" onClick={() => setOpen(false)}>List Your Business</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}